import React, { useEffect, useState } from 'react';
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  FormControl,
  FormLabel
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Autocomplete, TextField } from '@mui/material';
import { ArrowDownIcon } from '@components/icons/svg-icon.tsx';
import AppDialog from '@components/app-dialog/app-dialog.tsx';
import { ChromecastDevice, ChromecastAssignment } from '@api/casting.api.ts';
import { getRoomTVsGetRooms } from '@api/room-tvs-api/devices-api.ts';
import { formatIpAddress } from '@modules/casting/utils/casting.utils.ts';

interface Room {
  id: number;
  number: string;
  title: string;
}

interface AssignChromecastDialogProps {
  open: boolean;
  onClose: () => void;
  chromecast: ChromecastDevice | null;
  assignments: ChromecastAssignment[];
  onAssign: (
    chromecastUuid: string,
    roomNumber: string,
    chromecastName?: string,
    chromecastModel?: string,
    lastKnownIp?: string
  ) => Promise<boolean>;
  loading?: boolean;
}

interface FormData {
  room: Room | null;
}

const AssignChromecastDialog: React.FC<AssignChromecastDialogProps> = ({
  open,
  onClose,
  chromecast,
  assignments,
  onAssign,
  loading = false
}) => {
  const { t } = useTranslation();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);

  const { control, handleSubmit, reset, watch } = useForm<FormData>({
    mode: 'onTouched',
    defaultValues: {
      room: null
    }
  });

  const selectedRoom = watch('room');

  // Get rooms that are already assigned
  const assignedRoomNumbers = new Set(assignments.map((a) => a.roomNumber));

  // Filter out already assigned rooms
  const availableRooms = rooms.filter(
    (room) => !assignedRoomNumbers.has(room.number)
  );

  useEffect(() => {
    if (open) {
      fetchRooms();
      reset({ room: null });
    }
  }, [open, reset]);

  const fetchRooms = async () => {
    try {
      setLoadingRooms(true);
      const response = await getRoomTVsGetRooms();
      const roomsList =
        response.rows?.map((el: any) => ({
          id: el.id,
          number: el.number,
          title: `${t('room', 'Room')} ${el.number}`
        })) || [];
      setRooms(roomsList);
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
    } finally {
      setLoadingRooms(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    if (!chromecast || !data.room) return;

    const success = await onAssign(
      chromecast.id,
      data.room.number,
      chromecast.name,
      chromecast.model,
      chromecast.ip
    );

    if (success) {
      onClose();
    }
  };

  if (!chromecast) return null;

  const dialogContent = (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="body1"
          className="max-lg-xl:text-[14px]!"
          color="textSecondary"
          gutterBottom
          sx={{ fontWeight: 500 }}
        >
          {t('casting_device_details', 'Device Details')}
        </Typography>
        <Box
          sx={{
            bgcolor: 'grey.100',
            borderRadius: 1,
            p: 2,
            '& > div': { mb: 1, '&:last-child': { mb: 0 } }
          }}
        >
          <Box display="flex" justifyContent="space-between">
            <Typography
              variant="body1"
              color="textSecondary"
              className="font-medium! max-lg-xl:text-[14px]!"
            >
              {t('casting_chromecast_id', 'Chromecast ID')}:
            </Typography>
            <Typography
              variant="body1"
              className="text-(--blue-4)! font-medium! max-lg-xl:text-[14px]!"
              fontFamily="monospace"
              sx={{
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {chromecast.id}
            </Typography>
          </Box>
          {chromecast.model && (
            <Box display="flex" justifyContent="space-between">
              <Typography
                variant="body1"
                color="textSecondary"
                className="font-medium! max-lg-xl:text-[14px]!"
              >
                {t('casting_model', 'Model')}:
              </Typography>
              <Typography
                variant="body1"
                className="text-(--blue-4)! font-medium! max-lg-xl:text-[14px]!"
              >
                {chromecast.model}
              </Typography>
            </Box>
          )}
          <Box display="flex" justifyContent="space-between">
            <Typography
              variant="body1"
              color="textSecondary"
              className="font-medium! max-lg-xl:text-[14px]!"
            >
              {t('casting_ip_address', 'IP Address')}:
            </Typography>
            <Typography
              variant="body1"
              fontFamily="monospace"
              className="text-(--blue-4)! font-medium! max-lg-xl:text-[14px]!"
            >
              {formatIpAddress(chromecast.ip)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <FormControl fullWidth disabled={loadingRooms || loading}>
        <FormLabel>{t('casting_select_room', 'Select Room')}</FormLabel>
        <Controller
          name="room"
          control={control}
          render={({ field }) => (
            <Autocomplete
              {...field}
              disabled={loadingRooms || loading}
              options={availableRooms}
              getOptionLabel={(option) => option.title}
              isOptionEqualToValue={(option, value) =>
                option.number === value?.number
              }
              onChange={(_, value) => field.onChange(value)}
              popupIcon={<ArrowDownIcon sx={{ width: '18px !important' }} />}
              noOptionsText={
                loadingRooms
                  ? t('loading', 'Loading...')
                  : t('casting_no_rooms_available', 'No rooms available')
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t(
                    'casting_select_room_placeholder',
                    'Select a room...'
                  )}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingRooms ? (
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          )}
        />
      </FormControl>

      {availableRooms.length === 0 && !loadingRooms && (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ mt: 1, display: 'block' }}
        >
          {t(
            'casting_all_rooms_assigned',
            'All rooms already have Chromecasts assigned. Unassign a device to free up a room.'
          )}
        </Typography>
      )}
    </Box>
  );

  const dialogActions = (
    <div className="flex items-center gap-6 mt-4">
      <Button onClick={onClose} variant="outlined" disabled={loading}>
        {t('cancel', 'Cancel')}
      </Button>
      <Button
        onClick={handleSubmit(onSubmit)}
        variant="contained"
        disabled={!selectedRoom || loading}
        startIcon={loading ? <CircularProgress size={16} /> : null}
      >
        {loading
          ? t('assigning', 'Assigning...')
          : t('casting_assign', 'Assign')}
      </Button>
    </div>
  );

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={t('casting_assign_device', 'Assign Chromecast to Room')}
      content={dialogContent}
      actions={dialogActions}
      size="sm"
    />
  );
};

export default AssignChromecastDialog;
